'use client'

import { type FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { useRouter } from 'next/navigation'
import { useSession, useTranslator } from '@/hooks'
import { Img } from '@/components'
import { mq, presets, rgbAlpha, vw } from '@/styles'
import { LANG, STORE_ABBR } from '@/utils'
import type { TypeCmsSearchResults } from '@/schema'
const localStorageItem = `${STORE_ABBR}_${LANG}_search_history`

type Props = {
  data: TypeCmsSearchResults
  close: () => void
}

const resultIconByType = {
  search: () => ({
    type: 'svg' as const,
    src: '/images/svg/i--search-result-lupa.svg',
    alt: 'Search go page icon',
    size: { width: 40, height: 40 },
  }),
  categories: () => ({
    type: 'svg' as const,
    src: '/images/svg/i--search-result-category.svg',
    alt: 'Search result - category',
    size: { width: 16, height: 16 },
  }),
  brands: (item) => item?.logo,
  articles: () => ({
    type: 'svg' as const,
    src: '/images/svg/i--search-result-article.svg',
    alt: 'Search result - blog article',
    size: { width: 16, height: 16 },
  }),
  variants: (item) => item?.media?.[0]?.medium,
}

const pathnameByType = {
  categories: '/',
  brand: '/brand/',
  variants: '/product/',
}

const getSlug = (item) =>
  item?.product?.slug?.[LANG] ?? item?.slug?.[LANG] ?? ''

const getHistory = (raw = true) => {
  try {
    const stored = localStorage.getItem(localStorageItem)
    const parsed = JSON.parse(stored ?? '{}') || {}
    if (raw) return parsed

    const variantsArray = Object.values(parsed)
    return { variants: variantsArray }
  } catch (err) {
    console.error('Failed to read localStorage:', err)
    return raw ? {} : { variants: [] }
  }
}

const updateSearchHistory = (item) => {
  const slug = getSlug(item)
  if (!slug) return

  const current = getHistory()
  if (!current[slug]) {
    const updated = { ...current, [slug]: item }
    localStorage.setItem(localStorageItem, JSON.stringify(updated))
  }
}

const getListItems = (isCustomer, data, close, push, isHistory = false) => {
  const items = []

  for (const type of Object.keys(resultIconByType)) {
    const group = data[type]
    if (!Array.isArray(group)) continue

    for (const item of group) {
      const slug = getSlug(item)
      const media = resultIconByType[type](item)
      const hrefBase = isCustomer ? '/shop' : '/catalog'
      const href = slug
        ? `${hrefBase}${pathnameByType[type] ?? ''}${slug}?v=${item.name}`
        : item.href

      items.push(
        <ListItem
          key={`${type}-${item?.bigcommerceId ?? item?.name}`}
          onClick={() => {
            if (type === 'variants') updateSearchHistory(item)
            else if (type === 'search') {
              push({ pathname: '/', search: href })
            }
            if (type !== 'search') close()
          }}
        >
          <a
            href={
              href && type !== 'brands'
                ? href
                : isCustomer
                  ? `/shop/brand/${slug}`
                  : `/catalog/brand/${slug}`
            }
          >
            {media ? (
              <Img
                media={media}
                fill={false}
                unoptimized={isHistory}
              />
            ) : (
              <div className='placeholder' />
            )}
            <span>
              {type === 'variants' ? `${item?.product?.name} - ` : ''}{item?.name}
            </span>
          </a>
        </ListItem>
      )
    }
  }

  return items
}

export const SearchResults: FC<Props> = ({ data = {}, close }) => {
  const { customerToken } = useSession()
  const { push } = useRouter()
  const { t } = useTranslator()

  const [_historyItemsLength, setHistoryItemsLength] = useState<number>(Object.keys(getHistory())?.length || 0)

  const ListItems = useMemo(
    () => getListItems(!!customerToken, data, close, push),
    [customerToken, data, close, push]
  )

  const HistoryItems = []
  // const HistoryItems = useMemo(
  //   () =>
  //     typeof historyItemsLength !== 'number' || historyItemsLength > 0
  //       ? getListItems(!!customerToken, getHistory(false), close, push, true)
  //       : [],
  //   [historyItemsLength, customerToken, close, push]
  // )

  const onClickClearHistory = () => {
    localStorage.removeItem(localStorageItem)
    setHistoryItemsLength(0)
  }

  return (
    <List>
      {ListItems?.length > 0 ? (
        <>
          <dt>{t('search.search_results')}</dt>
          {ListItems}
        </>
      ) : (
        HistoryItems?.length > 0 && (
          <>
            <dt>
              {t('search.history')}.{' '}
              <button onClick={onClickClearHistory}>
                {t('actions.delete')}
              </button>
            </dt>
            {HistoryItems}
          </>
        )
      )}
    </List>
  )
}

const List = styled.dl`
  grid-column: 1 / span 6;
  grid-row: 2 / span 1;
  overflow: auto;
  padding: 0 0 ${vw(20, 'mobile')};
  margin-bottom: 0;

  ${mq.greaterThan('nexus7')} {
    padding: 0 0 ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 2 / span 5;
    padding: 0 0 ${vw(50, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding: 0 0 50px;
  }

  dt {
    background: var(--color-white);
    color: var(--color-text--darker);
    font-family: var(--font-graphik);
    font-weight: 400;
    margin-bottom: ${vw(20, 'mobile')};
    padding-bottom: 4px;
    padding-top: ${vw(20, 'mobile')};
    position: sticky;
    top: 0;
    z-index: 2;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
      padding-top: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(50, 'desktop')};
      padding-top: ${vw(50, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 50px;
      padding-top: 50px;
    }

    button {
      color: ${rgbAlpha('--color-text', 0.5)};
      text-decoration: underline;
      transition: 300ms color ease-out;

      &:hover {
        color: ${rgbAlpha('--color-text', 1)};
      }
    }
  }
`

const ListItem = styled(motion.dd)`
  color: var(--color-text--darker);
  font-family: var(--font-graphik);
  font-weight: 500;
  margin-bottom: ${vw(10, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(10, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 10px;
  }

  a {
    ${presets.font15}
    align-items: center;
    color: inherit;
    display: flex;
    justify-content: flex-start;
    min-height: 32px;
    text-decoration: none;
    width: 100%;

    &:hover {
      span {
        opacity: 1;
      }
    }

    span {
      opacity: .7;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: 300ms opacity ease-out;
      white-space: nowrap;
    }
  }

  .placeholder,
  figure,
  img {
    align-items: center;
    display: flex;
    height: ${vw(40, 'mobile')};
    justify-content: center;
    margin-right: ${vw(15, 'mobile')};
    object-fit: contain;
    transition: 350ms border-color ease-out;
    width: ${vw(40, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(40, 'tablet')};
      margin-right: ${vw(15, 'tablet')};
      width: ${vw(40, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(40, 'desktop')};
      margin-right: ${vw(15, 'desktop')};
      width: ${vw(40, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 40px;
      margin-right: 15px;
      width: 40px;
    }
  }

  .placeholder {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.05);
  }
`
